/**
 * Starts the workspace's in-pod browser (feature `browser`) when the Browser
 * view opens, and reports when its KasmVNC page answers.
 *
 * Probe `/proxy/6901/` first; if it isn't up, POST tmux-api's
 * `/browser/start` (runs `sanctum-browser start` in the pod) through the
 * same `/workspace/{id}/terminal-api/` route and auth as the terminal tabs,
 * then re-probe until the page answers. Plain fetch, as in useTerminalTabs:
 * these are workspace routes, not the API, and must never trigger the api
 * client's global logout on a 401.
 */

import { useEffect, useState } from 'react';
import { z } from 'zod';
import { STORAGE_KEYS } from '@utils/constants';

const WAIT_INTERVAL_MS = 1000;
const WAIT_TRIES = 20;

export type BrowserSession =
  | { phase: 'checking' }
  | { phase: 'starting' }
  | { phase: 'running' }
  | { phase: 'failed'; error: string };

const StartErrorSchema = z.object({ error: z.string() });

async function probe(src: string): Promise<boolean> {
  try {
    // GET, not HEAD: KasmVNC's web server answers HEAD with a 404.
    const response = await fetch(src, { method: 'GET', cache: 'no-store' });
    void response.body?.cancel().catch(() => undefined);
    return response.status === 200;
  } catch {
    return false;
  }
}

async function requestStart(workspaceId: string): Promise<string | null> {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  try {
    const response = await fetch(`/workspace/${workspaceId}/terminal-api/browser/start${query}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) return null;
    const parsed = StartErrorSchema.safeParse(await response.json().catch(() => null));
    return parsed.success ? parsed.data.error : `start failed (HTTP ${String(response.status)})`;
  } catch {
    return 'could not reach the workspace to start the browser';
  }
}

export function useBrowserSession(
  workspaceId: string,
  src: string,
): { session: BrowserSession; retry: () => void } {
  const [session, setSession] = useState<BrowserSession>({ phase: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Read through a function: the cleanup flips it while run() awaits, which
    // flow narrowing of a plain variable can't see.
    const life = { cancelled: false };
    const stopped = (): boolean => life.cancelled;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const wait = (): Promise<void> =>
      new Promise((resolve) => { timer = setTimeout(resolve, WAIT_INTERVAL_MS); });

    const run = async (): Promise<void> => {
      setSession({ phase: 'checking' });
      if (await probe(src)) {
        if (!stopped()) setSession({ phase: 'running' });
        return;
      }
      if (stopped()) return;
      setSession({ phase: 'starting' });
      const error = await requestStart(workspaceId);
      if (stopped()) return;
      if (error !== null) {
        setSession({ phase: 'failed', error });
        return;
      }
      for (let i = 0; i < WAIT_TRIES; i += 1) {
        await wait();
        if (stopped()) return;
        if (await probe(src)) {
          if (!stopped()) setSession({ phase: 'running' });
          return;
        }
        if (stopped()) return;
      }
      setSession({ phase: 'failed', error: 'the browser started but its page did not answer' });
    };

    void run();
    return () => {
      life.cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [workspaceId, src, attempt]);

  return { session, retry: () => { setAttempt((n) => n + 1); } };
}
